import * as React from 'react'

import {
  BoldIcon,
  CheckIcon,
  ChevronDownIcon,
  GripVerticalIcon,
  ItalicIcon,
  Trash2Icon,
  UnderlineIcon,
} from 'lucide-react'

import { Button } from '~/client/src/shared/components/Button/Button'
import { Kbd } from '~/client/src/shared/components/shadcn-ui/kbd'
import {
  Popover,
  PopoverTrigger,
} from '~/client/src/shared/components/shadcn-ui/popover'
import {
  Tooltip,
  TooltipTrigger,
} from '~/client/src/shared/components/shadcn-ui/tooltip'
import Localization from '~/client/src/shared/localization/LocalizationManager'

import type { IStylePreview } from './MarkupEditorStore'
import {
  MarkupPopoverContent as PopoverContent,
  MarkupTooltipContent as TooltipContent,
} from './MarkupPortalContainer'
import {
  ColorSegment,
  DIVIDER,
  MarkupPickerSheet,
  SEGMENT,
} from './markupSegments'
import MarkupToolButton from './MarkupToolButton'

interface IProps {
  color: string
  fillColor: string
  fontSize: number
  bold: boolean
  italic: boolean
  underline: boolean
  onColorChange: (color: string) => void
  onFillColorChange: (color: string) => void
  onFontSizeChange: (size: number) => void
  onToggleBold: () => void
  onToggleItalic: () => void
  onToggleUnderline: () => void
  onDelete: () => void
  // Omitted on mobile (pinned above the markup, not draggable) → drag grip hidden.
  onMovePointerDown?: (e: React.PointerEvent) => void
  // Touch: drop the keyboard "Del" keycap from the delete tooltip.
  isMobileEnv?: boolean
  // Mobile only: live-preview commit controls for the picker bottom sheets.
  stylePreview?: IStylePreview
}

const FONT_SIZES = [8, 10, 12, 14, 16, 18, 24, 32, 48, 72]

// Font-size uses a Popover, not Select: Select's body-portaled popup reads as an
// outside click and dismisses the Precise Location modal.
const MarkupTextContextToolbar: React.FC<IProps> = props => {
  const [sizeOpen, setSizeOpen] = React.useState(false)

  return (
    <div className="tw:flex tw:items-stretch tw:rounded-lg tw:bg-popover tw:shadow-lg">
      {/* Drag handle: lets the user slide the toolbar off the item it's covering.
        Hidden on mobile, where the toolbar is pinned above the markup. */}
      {props.onMovePointerDown && (
        <span
          aria-label={Localization.translator.moveToolbar}
          onPointerDown={props.onMovePointerDown}
          className={`tw:flex tw:cursor-grab tw:items-center tw:justify-center tw:px-1 tw:text-muted-foreground tw:active:cursor-grabbing ${DIVIDER}`}
        >
          <GripVerticalIcon className="tw:size-4" aria-hidden="true" />
        </span>
      )}
      {/* Box background (fill palette: transparents + no-fill restore). */}
      <ColorSegment
        color={props.fillColor}
        onChange={props.onFillColorChange}
        mode="fill"
        label={Localization.translator.backgroundColor}
        isMobileEnv={props.isMobileEnv}
        stylePreview={props.stylePreview}
      />
      {/* Text color — Google Docs style "A" over a bar showing the selected color. */}
      <ColorSegment
        color={props.color}
        onChange={props.onColorChange}
        glyph="A"
        label={Localization.translator.textColor}
        isMobileEnv={props.isMobileEnv}
        stylePreview={props.stylePreview}
      />

      <div className={`${SEGMENT} ${DIVIDER}`}>
        {(() => {
          const triggerInner = (
            <>
              {props.fontSize}
              <ChevronDownIcon
                className="tw:text-muted-foreground"
                aria-hidden
              />
            </>
          )
          // Single-choice list: desktop closes on pick. Mobile previews live but
          // stays open so the user can compare sizes and Apply.
          const body = (
            <div className="tw:flex tw:flex-col">
              {FONT_SIZES.map(size => (
                <Button
                  key={size}
                  type="button"
                  variant="ghost"
                  size="sm"
                  aria-pressed={size === props.fontSize}
                  onClick={() => {
                    props.onFontSizeChange(size)
                    if (!props.isMobileEnv) setSizeOpen(false)
                  }}
                  className={
                    size === props.fontSize
                      ? 'tw:w-full tw:justify-between tw:gap-4 tw:bg-primary/8 tw:font-normal tw:text-primary tw:hover:bg-primary/8'
                      : 'tw:w-full tw:justify-between tw:gap-4 tw:font-normal tw:text-foreground'
                  }
                >
                  {size}
                  {size === props.fontSize && (
                    <CheckIcon className="tw:size-4" aria-hidden="true" />
                  )}
                </Button>
              ))}
            </div>
          )
          // Symmetric leading/trailing slots keep the value centered whether or not
          // the selected row's check is shown.
          const mobileBody = (
            <div className="tw:flex tw:flex-col">
              {FONT_SIZES.map(size => (
                <Button
                  key={size}
                  type="button"
                  variant="ghost"
                  aria-pressed={size === props.fontSize}
                  onClick={() => props.onFontSizeChange(size)}
                  className={`tw:h-10 tw:w-full tw:justify-between tw:rounded tw:px-3 tw:font-medium tw:text-foreground ${
                    size === props.fontSize
                      ? 'tw:bg-primary/8 tw:hover:bg-primary/8'
                      : ''
                  }`}
                >
                  <span aria-hidden className="tw:size-6 tw:shrink-0" />
                  <span className="tw:text-sm tw:leading-5 tw:tracking-[-0.084px] tw:tabular-nums">
                    {size}
                  </span>
                  <span className="tw:flex tw:size-6 tw:shrink-0 tw:items-center tw:justify-center">
                    {size === props.fontSize && (
                      <CheckIcon
                        className="tw:size-4 tw:text-primary"
                        aria-hidden="true"
                      />
                    )}
                  </span>
                </Button>
              ))}
            </div>
          )
          return props.isMobileEnv ? (
            <MarkupPickerSheet
              open={sizeOpen}
              onOpenChange={setSizeOpen}
              title={Localization.translator.textSize}
              stylePreview={props.stylePreview}
              trigger={
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  aria-label={Localization.translator.fontSize}
                  className="tw:gap-1 tw:font-medium"
                >
                  {triggerInner}
                </Button>
              }
            >
              {mobileBody}
            </MarkupPickerSheet>
          ) : (
            <Popover open={sizeOpen} onOpenChange={setSizeOpen}>
              <PopoverTrigger
                render={
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    aria-label={Localization.translator.fontSize}
                    className="tw:gap-1 tw:font-medium"
                  />
                }
              >
                {triggerInner}
              </PopoverTrigger>
              <PopoverContent align="start" className="tw:w-20 tw:p-1">
                {body}
              </PopoverContent>
            </Popover>
          )
        })()}
      </div>

      <div className={`${SEGMENT} ${DIVIDER}`}>
        <MarkupToolButton
          icon={BoldIcon}
          label={Localization.translator.bold}
          isActive={props.bold}
          onClick={props.onToggleBold}
        />
        <MarkupToolButton
          icon={ItalicIcon}
          label={Localization.translator.italic}
          isActive={props.italic}
          onClick={props.onToggleItalic}
        />
        <MarkupToolButton
          icon={UnderlineIcon}
          label={Localization.translator.underline}
          isActive={props.underline}
          onClick={props.onToggleUnderline}
        />
      </div>

      <div className="tw:flex tw:items-center tw:p-1">
        <Tooltip>
          <TooltipTrigger
            render={
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={Localization.translator.delete}
                onClick={props.onDelete}
                className="tw:text-destructive tw:hover:text-destructive"
              />
            }
          >
            <Trash2Icon aria-hidden="true" />
          </TooltipTrigger>
          <TooltipContent>
            {Localization.translator.delete}
            {!props.isMobileEnv && <Kbd>Del</Kbd>}
          </TooltipContent>
        </Tooltip>
      </div>
    </div>
  )
}

export default MarkupTextContextToolbar
